import {ApiClientError, apiRequest} from '@/api/client';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {Spinner} from '@/components/ui/Spinner';
import {authApi} from '@/api/auth';
import {useAuth} from '@/contexts/AuthContext';
import {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';

type AcceptState = 'loading' | 'registration' | 'success' | 'error' | 'expired' | 'invalid' | 'verify-email';

interface AcceptResponse {
    teamMemberId: string;
    email: string;
    requiresSignup: boolean;
    requiresLogin: boolean;
}

const registerSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters')
});

type RegisterFormData = z.infer<typeof registerSchema>;

function SuccessIcon() {
    return (
        <svg fill="none" height="72" viewBox="0 0 24 24" width="72">
            <circle cx="12" cy="12" r="10" stroke="#006644" strokeWidth="1.5" />
            <path
                d="M8 12L11 15L16 9"
                stroke="#006644"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
        </svg>
    );
}

function ErrorIcon() {
    return (
        <svg fill="none" height="72" viewBox="0 0 24 24" width="72">
            <circle cx="12" cy="12" r="10" stroke="#F50B23" strokeWidth="1.5" />
            <path
                d="M12 8V12M12 16H12.01"
                stroke="#F50B23"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
        </svg>
    );
}

function ExpiredIcon() {
    return (
        <svg fill="none" height="72" viewBox="0 0 24 24" width="72">
            <circle cx="12" cy="12" r="10" stroke="#B25000" strokeWidth="1.5" />
            <path
                d="M12 6V12L16 14"
                stroke="#B25000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
        </svg>
    );
}

function TribeTaleLogo({style}: { style?: React.CSSProperties }) {
    return (
        <svg
            fill="none"
            style={style}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g clipPath="url(#clip0_tribetale_accept)">
                <path d="M11.7129 3.90332C18.1779 3.90332 23.4254 9.15083 23.4254 15.6159H11.7129V3.90332Z" fill="white"/>
                <path d="M11.7129 19.5205C5.24786 19.5205 0.000349045 14.273 0.000349045 7.80797L11.7129 7.80797L11.7129 19.5205Z" fill="white"/>
                <path d="M19.5205 11.7124C19.5205 18.1774 14.273 23.4249 7.80797 23.4249V11.7124H19.5205Z" fill="white" opacity="0.5"/>
                <path d="M3.9043 11.7124C3.9043 5.24737 9.1518 -0.000139236 15.6168 -0.000139236V11.7124H3.9043Z" fill="white" opacity="0.5"/>
            </g>
            <defs>
                <clipPath id="clip0_tribetale_accept">
                    <rect fill="white" height="23.4251" width="23.4251"/>
                </clipPath>
            </defs>
        </svg>
    );
}

export function AcceptInvitePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {user, isLoading: authLoading} = useAuth();
    const token = searchParams.get('token');
    const [state, setState] = useState<AcceptState>('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [acceptData, setAcceptData] = useState<AcceptResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [wrongAccount, setWrongAccount] = useState(false);

    const {
        register,
        handleSubmit,
        formState: {errors}
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema)
    });

    const isHandlingRegistration = useRef(false);

    useEffect(() => {
        if (isHandlingRegistration.current) {
            return;
        }

        if (authLoading) {
            return;
        }

        if (!token) {
            setState('invalid');
            setErrorMessage('No invitation token provided.');
            return;
        }

        const checkInvitation = async () => {
            try {
                const response = await apiRequest<AcceptResponse>('/team/invite/accept', {
                    method: 'POST',
                    body: JSON.stringify({token})
                });

                setAcceptData(response);

                if (response.requiresSignup) {
                    setState('registration');
                } else if (response.requiresLogin) {
                    if (user) {
                        if (user.email.toLowerCase() === response.email.toLowerCase()) {
                            await completeAcceptance(response.teamMemberId);
                        } else {
                            setWrongAccount(true);
                            setState('error');
                            setErrorMessage(`This invitation was sent to ${response.email}. You're currently logged in as ${user.email}. Please sign out and try again.`);
                        }
                    } else {
                        const returnUrl = encodeURIComponent(`/team/accept?token=${token}`);
                        navigate(`/login?returnTo=${returnUrl}`);
                    }
                } else {
                    await completeAcceptance(response.teamMemberId);
                }
            } catch (err) {
                handleError(err);
            }
        };

        checkInvitation();
    }, [token, navigate, authLoading, user]);

    const handleError = (err: unknown) => {
        if (err instanceof ApiClientError) {
            if (err.code === 'INVITATION_EXPIRED' || err.message.includes('expired')) {
                setState('expired');
                setErrorMessage(err.message);
            } else if (err.code === 'INVALID_TOKEN' || err.code === 'INVITATION_NOT_FOUND' || err.message.includes('invalid') || err.message.includes('not found')) {
                setState('invalid');
                setErrorMessage(err.message);
            } else {
                setState('error');
                setErrorMessage(err.message);
            }
        } else {
            setState('error');
            setErrorMessage('Something went wrong. Please try again later.');
        }
    };

    const completeAcceptance = async (teamMemberId: string) => {
        try {
            await apiRequest('/team/invite/complete', {
                method: 'POST',
                body: JSON.stringify({teamMemberId})
            });
            setState('success');
        } catch (err) {
            handleError(err);
        }
    };

    const onSubmit = async (data: RegisterFormData) => {
        if (!acceptData) {
            return;
        }

        isHandlingRegistration.current = true;
        setIsSubmitting(true);
        setErrorMessage('');

        try {
            await authApi.register(acceptData.email, data.name, `/team/accept?token=${encodeURIComponent(token || '')}`);
            setState('verify-email');
        } catch (err) {
            isHandlingRegistration.current = false;
            if (err instanceof ApiClientError) {
                setErrorMessage(err.message);
            } else {
                setErrorMessage('Could not send your sign-in link. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const containerStyle: React.CSSProperties = {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#fff',
        padding: '0 2.4rem',
        fontFamily: '\'Plus Jakarta Sans\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif'
    };

    const cardStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 500,
        width: '100%',
        padding: '0 5%',
        textAlign: 'center'
    };

    if (state === 'loading') {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>
                    <div style={{marginBottom: 24}}>
                        <Spinner />
                    </div>
                    <h1
                        style={{
                            fontSize: '2rem',
                            fontWeight: 700,
                            color: '#15171A',
                            margin: '0 0 8px 0',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Verifying invitation...
                    </h1>
                    <p style={{fontSize: '1.5rem', color: '#7C8B9A', margin: 0, lineHeight: 1.5}}>
                        Please wait while we verify your invitation.
                    </p>
                </div>
            </div>
        );
    }

    if (state === 'registration' && acceptData) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>
                    <header style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <div
                            style={{
                                width: 52,
                                height: 52,
                                backgroundColor: '#113139',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 20
                            }}
                        >
                            <TribeTaleLogo style={{width: 40, height: 40}} />
                        </div>
                        <h1
                            style={{
                                fontSize: '2.8rem',
                                fontWeight: 700,
                                letterSpacing: '-0.025em',
                                lineHeight: '1.15em',
                                color: '#15171A',
                                textAlign: 'center',
                                margin: '0 0 8px 0',
                                padding: 0
                            }}
                        >
                            Create your account
                        </h1>
                        <p
                            style={{
                                fontSize: '1.5rem',
                                color: '#7C8B9A',
                                margin: '0 0 32px 0',
                                lineHeight: 1.5
                            }}
                        >
                            We&apos;ll email you a sign-in link to join the team
                        </p>
                    </header>

                    <form style={{width: '100%'}} noValidate onSubmit={handleSubmit(onSubmit)}>
                        <div style={{marginBottom: '1.5rem', textAlign: 'left'}}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '1.3rem',
                                    fontWeight: 600,
                                    color: '#15171A',
                                    marginBottom: 4
                                }}
                            >
                                Email address
                            </label>
                            <input
                                style={{
                                    width: '100%',
                                    height: 54,
                                    padding: '12px 16px',
                                    borderRadius: 8,
                                    fontSize: '1.8rem',
                                    background: '#E5E9ED',
                                    border: '1px solid #E5E9ED',
                                    color: '#7C8B9A',
                                    letterSpacing: '-0.006em',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit',
                                    cursor: 'not-allowed'
                                }}
                                type="email"
                                value={acceptData.email}
                                disabled
                            />
                        </div>

                        <div style={{marginBottom: '1.5rem', textAlign: 'left'}}>
                            <label
                                htmlFor="name"
                                style={{
                                    display: 'block',
                                    fontSize: '1.3rem',
                                    fontWeight: 600,
                                    color: '#15171A',
                                    marginBottom: 4
                                }}
                            >
                                Full name
                            </label>
                            <input
                                {...register('name')}
                                autoComplete="name"
                                id="name"
                                placeholder="Your name"
                                style={{
                                    width: '100%',
                                    height: 54,
                                    padding: '12px 16px',
                                    borderRadius: 8,
                                    fontSize: '1.8rem',
                                    background: '#f3f6f7',
                                    border: '1px solid #f3f6f7',
                                    color: '#15171A',
                                    letterSpacing: '-0.006em',
                                    outline: 'none',
                                    transition: 'border-color .15s linear, background-color .15s linear, box-shadow .15s linear',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit'
                                }}
                                type="text"
                                onBlur={(e) => {
                                    e.target.style.backgroundColor = '#f3f6f7';
                                    e.target.style.borderColor = '#f3f6f7';
                                    e.target.style.boxShadow = 'none';
                                }}
                                onFocus={(e) => {
                                    e.target.style.backgroundColor = '#fff';
                                    e.target.style.borderColor = '#EF5A2B';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
                                }}
                            />
                            {errors.name && (
                                <p style={{marginTop: 4, fontSize: '1.3rem', color: '#f50b23'}}>{errors.name.message}</p>
                            )}
                        </div>

                        <button
                            disabled={isSubmitting}
                            style={{
                                display: 'block',
                                width: '100%',
                                height: 52,
                                marginTop: 32,
                                borderRadius: 8,
                                border: 'none',
                                background: '#EF5A2B',
                                color: '#fff',
                                fontSize: '1.8rem',
                                fontWeight: 400,
                                letterSpacing: '-0.01em',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1,
                                transition: 'opacity 0.2s ease',
                                fontFamily: 'inherit',
                                WebkitFontSmoothing: 'subpixel-antialiased'
                            }}
                            type="submit"
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    (e.target as HTMLButtonElement).style.opacity = '0.9';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    (e.target as HTMLButtonElement).style.opacity = '1';
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8}}>
                                    <svg style={{animation: 'spin 1s linear infinite', width: 18, height: 18}} viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="4" style={{opacity: 0.25}} />
                                        <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor" style={{opacity: 0.75}} />
                                    </svg>
                                    Sending link...
                                </span>
                            ) : (
                                <>Send sign-in link &rarr;</>
                            )}
                        </button>
                    </form>

                    {errorMessage && (
                        <p
                            style={{
                                marginTop: 16,
                                color: '#f50b23',
                                fontSize: '1.4rem',
                                lineHeight: 1.5,
                                textAlign: 'center',
                                textWrap: 'balance'
                            }}
                        >
                            {errorMessage}
                        </p>
                    )}

                    <p
                        style={{
                            marginTop: 24,
                            fontSize: '1.4rem',
                            color: '#7C8B9A'
                        }}
                    >
                        Already have an account?{' '}
                        <Link
                            style={{color: '#EF5A2B', textDecoration: 'none'}}
                            to={`/login?returnTo=${encodeURIComponent(`/team/accept?token=${token}`)}`}
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
                <style>
                    {`@keyframes spin { to { transform: rotate(360deg); } }`}
                </style>
            </div>
        );
    }

    if (state === 'success') {
        return (
            <div style={containerStyle}>
                <div style={{...cardStyle, padding: '48px 40px', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
                    <div style={{marginBottom: 24}}>
                        <SuccessIcon />
                    </div>
                    <h1
                        style={{
                            fontSize: '2.2rem',
                            fontWeight: 700,
                            color: '#15171A',
                            margin: '0 0 12px 0',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Welcome to the team!
                    </h1>
                    <p
                        style={{
                            fontSize: '1.5rem',
                            color: '#7C8B9A',
                            margin: '0 0 32px 0',
                            lineHeight: 1.5
                        }}
                    >
                        Your account has been activated. You can now access your assigned newsletters.
                    </p>
                    <Link
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 48,
                            padding: '0 32px',
                            borderRadius: 40,
                            background: '#EF5A2B',
                            color: '#FFFFFF',
                            fontSize: '1.5rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.2s ease'
                        }}
                        to="/dashboard"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#D94E24'; 
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#EF5A2B'; 
                        }}
                    >
                        Go to dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (state === 'verify-email') {
        return (
            <div style={containerStyle}>
                <div style={{...cardStyle, padding: '48px 40px', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            background: 'rgba(239, 90, 43, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px'
                        }}
                    >
                        <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
                            <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="#EF5A2B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                        </svg>
                    </div>
                    <h1
                        style={{
                            fontSize: '2.2rem',
                            fontWeight: 700,
                            color: '#15171A',
                            margin: '0 0 12px 0',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Check your email
                    </h1>
                    <p
                        style={{
                            fontSize: '1.5rem',
                            color: '#7C8B9A',
                            margin: '0 0 12px 0',
                            lineHeight: 1.5
                        }}
                    >
                        Follow the sign-in link to create your account and accept the invitation.
                    </p>
                    <p
                        style={{
                            fontSize: '1.4rem',
                            color: '#7C8B9A',
                            margin: '0 0 32px 0',
                            lineHeight: 1.5
                        }}
                    >
                        We&apos;ve sent a sign-in link to <strong style={{color: '#394047'}}>{acceptData?.email}</strong>. Please check your inbox to continue.
                    </p>
                    <Link
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 48,
                            padding: '0 32px',
                            borderRadius: 40,
                            background: '#EF5A2B',
                            color: '#FFFFFF',
                            fontSize: '1.5rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.2s ease'
                        }}
                        to={`/login?returnTo=${encodeURIComponent(`/team/accept?token=${token}`)}`}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#D94E24'; 
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#EF5A2B'; 
                        }}
                    >
                        Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    if (state === 'expired') {
        return (
            <div style={containerStyle}>
                <div style={{...cardStyle, padding: '48px 40px', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
                    <div style={{marginBottom: 24}}>
                        <ExpiredIcon />
                    </div>
                    <h1
                        style={{
                            fontSize: '2.2rem',
                            fontWeight: 700,
                            color: '#15171A',
                            margin: '0 0 12px 0',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Invitation expired
                    </h1>
                    <p
                        style={{
                            fontSize: '1.5rem',
                            color: '#7C8B9A',
                            margin: '0 0 32px 0',
                            lineHeight: 1.5
                        }}
                    >
                        This invitation link has expired. Please contact the newsletter owner to request a new invitation.
                    </p>
                    <Link
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 44,
                            padding: '0 24px',
                            borderRadius: 40,
                            background: '#FFFFFF',
                            border: '1px solid #E5E9ED',
                            color: '#394047',
                            fontSize: '1.4rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.15s ease'
                        }}
                        to="/"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F4F5F6'; 
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#FFFFFF'; 
                        }}
                    >
                        Go to homepage
                    </Link>
                </div>
            </div>
        );
    }

    if (state === 'invalid') {
        return (
            <div style={containerStyle}>
                <div style={{...cardStyle, padding: '48px 40px', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
                    <div style={{marginBottom: 24}}>
                        <ErrorIcon />
                    </div>
                    <h1
                        style={{
                            fontSize: '2.2rem',
                            fontWeight: 700,
                            color: '#15171A',
                            margin: '0 0 12px 0',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Invalid invitation
                    </h1>
                    <p
                        style={{
                            fontSize: '1.5rem',
                            color: '#7C8B9A',
                            margin: '0 0 32px 0',
                            lineHeight: 1.5
                        }}
                    >
                        {errorMessage || 'This invitation link is not valid. It may have been used already or the link is incorrect.'}
                    </p>
                    <Link
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 44,
                            padding: '0 24px',
                            borderRadius: 40,
                            background: '#FFFFFF',
                            border: '1px solid #E5E9ED',
                            color: '#394047',
                            fontSize: '1.4rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.15s ease'
                        }}
                        to="/"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F4F5F6'; 
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#FFFFFF'; 
                        }}
                    >
                        Go to homepage
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={{...cardStyle, padding: '48px 40px', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
                <div style={{marginBottom: 24}}>
                    <ErrorIcon />
                </div>
                <h1
                    style={{
                        fontSize: '2.2rem',
                        fontWeight: 700,
                        color: '#15171A',
                        margin: '0 0 12px 0',
                        letterSpacing: '-0.02em'
                    }}
                >
                    {wrongAccount ? 'Wrong account' : 'Something went wrong'}
                </h1>
                <p
                    style={{
                        fontSize: '1.5rem',
                        color: '#7C8B9A',
                        margin: '0 0 32px 0',
                        lineHeight: 1.5
                    }}
                >
                    {errorMessage || 'We couldn\'t process your invitation. Please try again later or contact support.'}
                </p>
                <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center'}}>
                    {wrongAccount ? (
                        <button style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 48,
                            padding: '0 32px',
                            borderRadius: 40,
                            background: '#EF5A2B',
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: '1.5rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit'
                        }}
                        type="button"
                        onClick={async () => {
                            await authApi.signOut();
                            const returnUrl = encodeURIComponent(`/team/accept?token=${token}`);
                            window.location.href = `/login?returnTo=${returnUrl}`;
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#D94E24'; 
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#EF5A2B'; 
                        }}
                        >
                            Sign out and try again
                        </button>
                    ) : (
                        <Link
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 44,
                                padding: '0 24px',
                                borderRadius: 40,
                                background: '#FFFFFF',
                                border: '1px solid #E5E9ED',
                                color: '#394047',
                                fontSize: '1.4rem',
                                fontWeight: 500,
                                textDecoration: 'none',
                                transition: 'all 0.15s ease'
                            }}
                            to="/"
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#F4F5F6'; 
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#FFFFFF'; 
                            }}
                        >
                            Go to homepage
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}
